import { CurrencyPipe } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, Observable, tap } from 'rxjs';
import { AuthorizeService } from 'src/app/auth/authorize.service';
import { InvoiceFilter } from 'src/app/common/InvoiceFilter';
import { InvoiceService } from 'src/app/services/invoice.service';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { Invoice } from 'src/app/common/invoice';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { AppProfileService } from 'src/app/services/profile.service';
import { ExtendedProfile } from 'src/app/common/ExtendedProfile';
import { InvoiceCategory, InvoiceCategoryConstants } from 'src/app/common/InvoiceCategory';
import { ShopCommonService } from 'src/app/services/shop-common.service';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { KENDO_GRID } from '@progress/kendo-angular-grid';
import { PagedList } from 'src/app/common/PagedList';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { RouterModule } from '@angular/router';
import { InvoiceSearchComponent } from '../invoice-search/invoice-search.component';
import { CallApiService } from '../../services/api-action.service';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { saveBlob } from 'src/app/common/browser-files';
import { GridPdfColumnsDirective } from '../grid/grid-pdf-columns.directive';
import { PdfPageComponent } from '../grid/pdf-page.component';
import { GridNoRecordsComponent } from '../grid/grid-no-records.component';

@Component({
    imports: [CurrencyPipe, KENDO_GRID, FontAwesomeModule, RouterModule, InvoiceSearchComponent, AsyncClickDirective, GridPdfColumnsDirective, PdfPageComponent, GridNoRecordsComponent],
    selector: 'app-invoice-list',
    templateUrl: './invoice-list.component.html',
    styleUrls: ['./invoice-list.component.css']
})
export class InvoiceListComponent implements OnInit {
  invoiceService = inject(InvoiceService);
  private commonService = inject(ShopCommonService);
  private profileService = inject(AppProfileService);
  private authService = inject(AuthorizeService);
  private confirmActionService = inject(CallApiService);

  private destroyRef = inject(DestroyRef);

  public readonly InvoiceCategoryConstants = InvoiceCategoryConstants;
  public faTimes = faTimes;
  // TODO: invalidate the list when the tenant mode changes to customer.
  public tenantMode$: Observable<boolean>;
  // Set by <app-invoice-search>, which emits its default criteria once the profile is there —
  // that is the first load.
  filter: InvoiceFilter;
  faFilePdf = faFilePdf;
  faDownload = faDownload;
  public profile: ExtendedProfile;
  private InvoiceCategories: InvoiceCategory[] = [];

  // #648: server-side paging/sorting; default order = newest first. Only a retailer has sales
  // invoices; anyone else gets the "no records" template.
  public list = new PagedList<Invoice>({
    sortFields: { invoiceNumber: 'invoiceNumber', invoiceDateTime: 'invoiceDateTime' },
    defaultSort: [{ field: 'invoiceDateTime', dir: 'desc' }],
    errorMessage: $localize`Fehler beim Ermitteln der Rechnungen`,
    fetch: (paging) =>
      this.filter && this.profile?.customer.isRetailer
        ? this.invoiceService
            .getSaleInvoicesBySearch(
              this.filter.dateFrom,
              this.filter.dateTo,
              this.filter.customerNameOrNumber,
              this.filter.invoiceGroupId,
              paging
            )
            .pipe(
              map((r) => {
                r.items.forEach((o) => (o.invoiceDateTime = new Date(o.invoiceDateTime)));
                return r;
              })
            )
        : null,
  });

  ngOnInit() {
    this.tenantMode$ = this.authService.$retailerMode;
    this.profileService.currentProfile$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((p) => {
      this.profile = p;
      if (this.filter) this.list.load();
    });

    this.InvoiceCategories = this.commonService.getInvoiceCategories();
  }

  search(filter: InvoiceFilter) {
    this.filter = filter;
    this.list.search();
  }

  public downloadInvoiceDocument(invoiceId: string) {
    this.invoiceService.download(invoiceId).subscribe((blob) => saveBlob(blob, invoiceId));
  }

  public getInvoiceCategoryDescription(invoice: Invoice): string {
    return this.InvoiceCategories.filter((ic) => ic.Id === invoice.invoiceCategory)[0].DisplayName;
  }

  // Returns a factory so [asyncClick] can guard this per-row cancel against a
  // double-click (the confirm() + request run when the returned fn is invoked).
  public onCancel(id: string): () => Observable<unknown> {
    return () =>
      this.confirmActionService.confirmAndCallWithToasts$(
        this.invoiceService.CancelInvoice({ InvoiceId: id }),
        $localize`Sind Sie sicher, dass Sie die Rechnung stornieren möchten?`,
        $localize`Rechnung wurde storniert.`,
        $localize`Beim Stornieren der Rechnung ist ein Fehler aufgetreten.`
      )
      .pipe(tap(() => this.list.load()));
  }
}
